/**
 * Pod Configuration — Revenue-Based Community Grouping
 *
 * Pods are derived from an organization's annual revenue range.
 * They are NOT stored entities — just a filter/grouping mechanism.
 *
 * Pod 1 = highest revenue (Strategy / $8M+)
 * Pod 2 = mid revenue (Leverage / $2M–$8M)
 * Pod 3 = entry revenue (Foundation / <$2M)
 */

import { z } from 'zod';

// ─── Revenue Range Enum ─────────────────────────────────────────────

export const revenueRangeSchema = z.enum(['under_2m', '2m_to_8m', 'over_8m']);
export type RevenueRange = z.infer<typeof revenueRangeSchema>;

// ─── Pod Types ──────────────────────────────────────────────────────

export type PodNumber = 1 | 2 | 3;
export const PODS = [1, 2, 3] as const;

// ─── Pod Names ──────────────────────────────────────────────────────

export const POD_NAMES: Record<PodNumber, string> = {
  1: 'Strategy',
  2: 'Leverage',
  3: 'Foundation',
};

export const POD_REVENUE_LABELS: Record<PodNumber, string> = {
  1: '$8M+',
  2: '$2M–$8M',
  3: 'Under $2M',
};

// ─── Derivation Functions ───────────────────────────────────────────

/**
 * Derive pod number from an org's annual revenue range.
 * Returns null if revenue range is unknown/unset.
 */
export function getPodNumber(revenueRange: string | null | undefined): PodNumber | null {
  switch (revenueRange) {
    case 'over_8m': return 1;
    case '2m_to_8m': return 2;
    case 'under_2m': return 3;
    default: return null;
  }
}

/**
 * Reverse lookup: pod number → revenue range value for DB queries.
 */
export function getRevenueRangeForPod(pod: PodNumber): RevenueRange {
  switch (pod) {
    case 1: return 'over_8m';
    case 2: return '2m_to_8m';
    case 3: return 'under_2m';
  }
}

// ─── Company Size Mapping ───────────────────────────────────────────
// Maps the triage question "Company size" answers to revenue ranges.
// Triage options: ["< $1M", "$1M - $10M", "$10M - $50M", "$50M+"]

/**
 * Derive revenue range from the existing company_size triage answer.
 * Used as a fallback when annual_revenue_range is not yet backfilled.
 */
export function revenueRangeFromCompanySize(companySize: string | null | undefined): RevenueRange | null {
  switch (companySize) {
    case '< $1M': return 'under_2m';
    case '$1M - $10M': return '2m_to_8m';
    case '$10M - $50M': return 'over_8m';
    case '$50M+': return 'over_8m';
    default: return null;
  }
}

// ─── Visibility ────────────────────────────────────────────────────

/**
 * Compute which pods a user can see based on their own pod level.
 * Users see their own pod and all lower-revenue pods (higher numbers).
 * Returns empty array if user has no pod (no revenue data).
 */
export function getVisiblePods(userPod: PodNumber | null): PodNumber[] {
  if (!userPod) return [];
  return PODS.filter(p => p >= userPod);
}

// ─── Display Helpers ────────────────────────────────────────────────

/**
 * Format pod label for display: "Pod 1" or "Pod 1 — Strategy"
 */
export function formatPodLabel(pod: PodNumber, includeSubtitle = false): string {
  if (includeSubtitle) {
    return `Pod ${pod} — ${POD_NAMES[pod]}`;
  }
  return `Pod ${pod}`;
}
